import {useLazyQuery} from "@apollo/client";
import get from "lodash/get";
import {useFormContext} from "react-hook-form";
import {FiSearch} from "react-icons/fi";
import {Link} from "react-router";
import {components, NoticeProps} from "react-select";

import {SelectField, SelectFieldBaseProps} from "@/components/form/fields/SelectField/SelectField.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {Select} from "@/components/select/Select.tsx";
import {useLoadOptions} from "@/lib/hooks/useLoadOptions.ts";
import {GQL_INTEGRATION_PROFILES, IntegrationProfilesResponse} from "@/lib/queries/contacts.ts";
import {IntegrationProfile} from "@/pages/contacts/components/form/types.ts";

interface IntegrationSelectFieldProps extends SelectFieldBaseProps<number> {
  integrationProfiles?: IntegrationProfile[];
  value?: number[];
  onChange?: (value: number[]) => void;
}

const IntegrationsNoOptionsMessage = (props: NoticeProps<IntegrationProfile>) => (
  <components.NoOptionsMessage {...props}>
    <Icon icon={FiSearch} className="pulse-color-gray-500 mb-3" removeDivDimensions />
    <div className="pulseTextBodyBold pulse-color-gray-800 mb-2">No Integrations Configured</div>
    <div className="pulseTextBody pulse-color-gray-700">
      To setup, go to{" "}
      <Link to="/integrations/manage/?tab=library" className="pulseTextBodyBold">
        Integrations
      </Link>
    </div>
  </components.NoOptionsMessage>
);

export const IntegrationSelectField = ({
  integrationProfiles: optionsProp,
  value: propsValue,
  onChange,
  name,
  placeholder = "Select Integrations...",
  ...rest
}: IntegrationSelectFieldProps) => {
  const {
    watch,
    setValue,
    formState: {defaultValues},
  } = useFormContext();
  const value = propsValue ? propsValue : (watch(name) as number[] | undefined);
  const defaultValue = get(defaultValues, name) as number[] | undefined;

  const [getIntegrationProfiles] = useLazyQuery<IntegrationProfilesResponse>(GQL_INTEGRATION_PROFILES);
  const {options: integrationProfiles, isLoading} = useLoadOptions<
    IntegrationProfile,
    IntegrationProfilesResponse,
    false
  >({
    options: optionsProp,
    fetchOptions: getIntegrationProfiles,
    transform: (result) => Array.from(result.data?.integrationProfiles || []),
  });

  return (
    <SelectField
      name={name}
      placeholder={placeholder}
      {...rest}
      select={
        <Select
          isMulti={true}
          isLoading={isLoading}
          options={integrationProfiles}
          getOptionLabel={(p: IntegrationProfile) => p.name}
          getOptionValue={(p: IntegrationProfile) => String(p.id)}
          defaultValue={integrationProfiles.filter((p) =>
            Array.isArray(defaultValue) ? defaultValue.includes(p.id) : false
          )}
          value={integrationProfiles.filter((p) => (Array.isArray(value) ? value.includes(p.id) : false))}
          onChange={(v) => {
            const selected = (v as IntegrationProfile[] | null) ?? [];
            const ids = selected.map((p) => p.id);
            if (onChange) {
              onChange(ids);
            } else {
              setValue(name, ids, {shouldDirty: true, shouldValidate: true});
            }
          }}
          components={integrationProfiles.length === 0 ? {NoOptionsMessage: IntegrationsNoOptionsMessage} : undefined}
        />
      }
    />
  );
};
